/**
 * Upload em lotes com retomada (RF-M2-007, RNF-02).
 *
 * Envia os objetos ao S3 via as URLs pré-assinadas PUT devolvidas pelo
 * POST /cargas. Executa em lotes paralelos limitados; em falha de rede, retenta
 * com backoff (1s, 2s, 4s) por objeto, sem perder o progresso já enviado — os
 * objetos concluídos não são reenviados (idempotência de upload).
 */

import { ArquivoPreparado, UploadInstructionObjeto } from './cargas-types';

export interface UploadProgresso {
  readonly enviados: number;
  readonly total: number;
}

const BACKOFF_MS = [1000, 2000, 4000];

/**
 * A URL pré-assinada expirou (o S3 responde 403): repetir não adianta — é preciso
 * pedir URLs novas (`POST /cargas/{id}/upload-urls`, US-08 AC4).
 */
export class UrlExpiradaError extends Error {
  constructor() {
    super('URL de upload expirada.');
    this.name = 'UrlExpiradaError';
  }
}

/** Envia um objeto via PUT pré-assinado; retenta em falha transitória. */
async function putComRetentativa(
  url: string,
  file: File,
  headers: Readonly<Record<string, string>>,
  putFn: typeof fetch,
): Promise<void> {
  let ultimoErro: unknown;
  for (let tentativa = 0; tentativa <= BACKOFF_MS.length; tentativa += 1) {
    try {
      const resp = await putFn(url, {
        method: 'PUT',
        body: file,
        // Exatamente os headers assinados pela API (checksum — RN-M2-002 — e
        // If-None-Match — RN-GLOBAL-001). Header a mais ou a menos = 403 do S3.
        headers: { ...headers },
      });
      if (resp.ok) {
        return;
      }
      // 412 (precondition failed) = objeto já existe (imutabilidade) → tratar como enviado.
      if (resp.status === 412) {
        return;
      }
      // 403 = assinatura expirada: não retenta, sobe para a renovação das URLs.
      if (resp.status === 403) {
        throw new UrlExpiradaError();
      }
      ultimoErro = new Error(`PUT falhou com status ${resp.status}`);
    } catch (err) {
      if (err instanceof UrlExpiradaError) {
        throw err;
      }
      ultimoErro = err;
    }
    if (tentativa < BACKOFF_MS.length) {
      await new Promise((r) => setTimeout(r, BACKOFF_MS[tentativa]));
    }
  }
  throw ultimoErro instanceof Error ? ultimoErro : new Error('Falha no upload do objeto.');
}

/**
 * Faz upload dos arquivos preparados usando as instruções por objeto. Mantém um
 * conjunto de caminhos já enviados (retomada) — reexecutar pula o que já subiu.
 *
 * @param concorrencia número de PUTs paralelos por lote.
 * @param jaEnviados caminhos já concluídos (retomada após falha).
 */
export async function uploadEmLotes(
  arquivos: readonly ArquivoPreparado[],
  instrucoes: readonly UploadInstructionObjeto[],
  opts: {
    onProgresso?: (p: UploadProgresso) => void;
    concorrencia?: number;
    jaEnviados?: Set<string>;
    putFn?: typeof fetch;
  } = {},
): Promise<Set<string>> {
  const concorrencia = opts.concorrencia ?? 5;
  const putFn = opts.putFn ?? fetch;
  const enviados = opts.jaEnviados ?? new Set<string>();
  const total = arquivos.length;

  const urlPorCaminho = new Map(instrucoes.map((i) => [i.caminho, i]));
  const pendentes = arquivos.filter((a) => !enviados.has(a.caminho));

  for (let i = 0; i < pendentes.length; i += concorrencia) {
    const lote = pendentes.slice(i, i + concorrencia);
    await Promise.all(
      lote.map(async (arq) => {
        const instr = urlPorCaminho.get(arq.caminho);
        if (!instr) {
          throw new Error('Instrução de upload ausente para um arquivo.');
        }
        await putComRetentativa(instr.url, arq.file, instr.headers, putFn);
        enviados.add(arq.caminho);
        opts.onProgresso?.({ enviados: enviados.size, total });
      }),
    );
  }
  return enviados;
}

/**
 * Envia com retomada completa: se as URLs expirarem no meio, pede URLs NOVAS só
 * para os arquivos que ainda não subiram (`renovar`) e continua — nada do que já
 * foi enviado é repetido. `jaEnviados` é do chamador e guarda o progresso entre
 * tentativas (inclusive quando a rede cai e o usuário clica em "Retomar envio").
 */
export async function enviarComRenovacao(
  arquivos: readonly ArquivoPreparado[],
  instrucoes: readonly UploadInstructionObjeto[],
  opts: {
    jaEnviados: Set<string>;
    renovar: (pendentes: readonly ArquivoPreparado[]) => Promise<readonly UploadInstructionObjeto[]>;
    onProgresso?: (p: UploadProgresso) => void;
    concorrencia?: number;
    putFn?: typeof fetch;
  },
): Promise<readonly UploadInstructionObjeto[]> {
  const { renovar, ...envio } = opts;
  try {
    await uploadEmLotes(arquivos, instrucoes, envio);
    return instrucoes;
  } catch (err) {
    if (!(err instanceof UrlExpiradaError)) {
      throw err;
    }
    const novas = await renovar(arquivos.filter((a) => !opts.jaEnviados.has(a.caminho)));
    await uploadEmLotes(arquivos, novas, envio);
    return novas;
  }
}

